'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { SkipBack, SkipForward, Play, Pause, Download } from 'lucide-react'
import { useStoryboard } from '../context'
import type { StoryboardSegment } from '../types'
import useStoryboardMediaSource from '../hooks/useStoryboardMediaSource'
import { ensureMediaRecordForPreview, getMediaObjectUrl } from '../utils/mediaCache'


const SegmentPreviewPanel: React.FC = () => {
  const { segments, activeSegmentId, selectSegment, focusedPreviewEntryIds } = useStoryboard();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isContinuousPlay, setIsContinuousPlay] = useState(false);
  const continuousPlayRef = useRef(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const activeSegment = useMemo(
    () => segments.find((segment) => segment.segmentId === activeSegmentId) ?? null,
    [segments, activeSegmentId]
  );

  const previewEntries = activeSegment?.previewEntries ?? [];
  const selectedPreviewId = activeSegment?.selectedPreviewId ?? null;
  const focusedPreviewId = activeSegment
    ? focusedPreviewEntryIds[activeSegment.segmentId] ?? null
    : null;
  const fallbackEntry = previewEntries[0] ?? null;
  const activePreviewId =
    focusedPreviewId ??
    selectedPreviewId ??
    (fallbackEntry ? fallbackEntry.previewId : null);
  const displayEntry =
    (activePreviewId
      ? previewEntries.find((entry) => entry.previewId === activePreviewId) ?? null
      : null) ?? fallbackEntry;

  const projectId = activeSegment?.projectId ?? null;
  const segmentId = activeSegment?.segmentId ?? null;

  const { src: mainMediaSrc } = useStoryboardMediaSource({
    projectId,
    segmentId,
    entry: displayEntry ?? null,
    autoFetch: true,
  });

  const activeSegmentIndex = useMemo(
    () => segments.findIndex((segment) => segment.segmentId === activeSegmentId),
    [segments, activeSegmentId]
  );
  const hasPreviousSegment = activeSegmentIndex > 0;
  const hasNextSegment = activeSegmentIndex >= 0 && activeSegmentIndex < segments.length - 1;

  const stopContinuousPlay = useCallback(() => {
    if (!continuousPlayRef.current) {
      return;
    }
    continuousPlayRef.current = false;
    setIsContinuousPlay(false);
  }, []);

  const startContinuousPlay = useCallback(() => {
    if (continuousPlayRef.current) {
      return;
    }
    continuousPlayRef.current = true;
    setIsContinuousPlay(true);
  }, []);

  const handleTogglePlayback = () => {
    const node = videoRef.current;
    if (!node) return;
    if (node.paused) {
      startContinuousPlay();
      const playPromise = node.play();
      if (playPromise) {
        playPromise.catch(() => {
          stopContinuousPlay();
        });
      }
    } else {
      stopContinuousPlay();
      node.pause();
    }
  };

  const handleGoToPreviousSegment = () => {
    if (!hasPreviousSegment) return;
    const previousSegment = segments[activeSegmentIndex - 1];
    if (previousSegment) {
      selectSegment(previousSegment.segmentId);
    }
  };

  const handleGoToNextSegment = () => {
    if (!hasNextSegment) return;
    const nextSegment = segments[activeSegmentIndex + 1];
    if (nextSegment) {
      selectSegment(nextSegment.segmentId);
    }
  };

  const showPauseState = isContinuousPlay || isPlaying;

  const handleDownloadVideo = useCallback(async () => {
    if (!projectId || !segmentId || !displayEntry) {
      setDownloadError('暂无可下载的视频');
      return;
    }
    setDownloading(true);
    setDownloadError(null);
    try {
      await ensureMediaRecordForPreview({
        projectId,
        segmentId,
        entry: displayEntry,
      });
      const media = await getMediaObjectUrl(displayEntry.previewId);
      if (media) {
        const { record, objectUrl } = media;
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = record.fileName ?? `preview_${displayEntry.previewId}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        return;
      }
      if (displayEntry.url) {
        const anchor = document.createElement('a');
        anchor.href = displayEntry.url;
        anchor.target = '_blank';
        anchor.rel = 'noopener';
        anchor.click();
        return;
      }
      throw new Error('未找到可下载的视频。');
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : '下载失败，请稍后重试。';
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  }, [projectId, segmentId, displayEntry]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => {
      setIsPlaying(false);
      if (!node.ended) {
        stopContinuousPlay();
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      if (!continuousPlayRef.current) {
        return;
      }
      let nextPlayableSegment: StoryboardSegment | undefined;
      for (let index = activeSegmentIndex + 1; index < segments.length; index += 1) {
        const candidate = segments[index];
        if (candidate.previewEntries.length > 0) {
          nextPlayableSegment = candidate;
          break;
        }
      }
      if (nextPlayableSegment) {
        selectSegment(nextPlayableSegment.segmentId);
      } else {
        stopContinuousPlay();
      }
    };

    node.addEventListener('play', handlePlay);
    node.addEventListener('pause', handlePause);
    node.addEventListener('ended', handleEnded);
    return () => {
      node.removeEventListener('play', handlePlay);
      node.removeEventListener('pause', handlePause);
      node.removeEventListener('ended', handleEnded);
    };
  }, [activeSegmentIndex, segments, selectSegment, stopContinuousPlay]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;

    setIsPlaying(false);

    if (!displayEntry) {
      node.pause();
      try {
        node.currentTime = 0;
      } catch {
        // ignore seek failures during source swap
      }
      stopContinuousPlay();
      return;
    }

    try {
      node.currentTime = 0;
    } catch {
      // ignore seek failures during source swap
    }

    if (!continuousPlayRef.current) {
      node.pause();
      return;
    }

    const tryPlay = () => {
      const playPromise = node.play();
      if (playPromise) {
        playPromise.catch(() => {
          stopContinuousPlay();
        });
      }
    };

    if (node.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      tryPlay();
      return;
    }

    const handleCanPlay = () => {
      node.removeEventListener('canplay', handleCanPlay);
      tryPlay();
    };

    node.addEventListener('canplay', handleCanPlay);
    return () => {
      node.removeEventListener('canplay', handleCanPlay);
    };
  }, [displayEntry, displayEntry?.previewId, stopContinuousPlay]);

  useEffect(() => {
    setDownloadError(null);
  }, [activePreviewId]);

  const renderPlaceholder = () => (
    <div className="flex-1 rounded-lg border border-dashed border-border min-h-[200px] md:min-h-[280px] bg-muted/20" />
  )

  return (
    <div className="flex-1 space-y-3">
      <div className="space-y-1.5">
        {displayEntry ? (
          <video
            ref={videoRef}
            src={mainMediaSrc ?? displayEntry.url ?? undefined}
            controls
            poster={activeSegment?.referenceFrame || undefined}
            style={{ width: '100%', borderRadius: 16, backgroundColor: '#000', maxHeight: 420 }}
          />
        ) : (
          renderPlaceholder()
        )}

        <div className="flex flex-col sm:flex-row gap-1 sm:gap-1.5 justify-center items-center">
          <Button
            variant="outline"
            disabled={!hasPreviousSegment}
            onClick={handleGoToPreviousSegment}
            className="w-full sm:w-auto sm:flex-1 min-w-[180px]"
          >
            <SkipBack className="mr-2 size-4" /> 切换到上一个单元
          </Button>
          <Button
            disabled={!displayEntry}
            onClick={handleTogglePlayback}
            className="w-full sm:w-auto sm:flex-1 min-w-[180px]"
          >
            {showPauseState ? (
              <>
                <Pause className="mr-2 size-4" /> 暂停
              </>
            ) : (
              <>
                <Play className="mr-2 size-4" /> 播放
              </>
            )}
          </Button>
          <Button
            variant="outline"
            disabled={!hasNextSegment}
            onClick={handleGoToNextSegment}
            className="w-full sm:w-auto sm:flex-1 min-w-[180px]"
          >
            <SkipForward className="mr-2 size-4" /> 切换到下一个单元
          </Button>
          <Button
            variant="outline"
            disabled={!displayEntry || downloading}
            onClick={handleDownloadVideo}
            className="w-full sm:w-auto sm:flex-1 min-w-[180px]"
          >
            <Download className="mr-2 size-4" /> {downloading ? '下载中…' : '下载当前视频'}
          </Button>
        </div>
        {downloadError ? (
          <div className="text-center text-xs text-destructive">{downloadError}</div>
        ) : null}
      </div>
    </div>
  )
};

export default SegmentPreviewPanel;
